/**
 * Presenter keys for the whole app, called once from the Shell. Creates the
 * presenter session with the real stores, listens for keydown in the
 * capture phase on window, follows useUI.presenter (leaving presenter mode
 * stops the tour and unfreezes) and unfreezes on page unload so the twin's
 * persisted data source is not left 'off'.
 *
 * The listener is added in a layout effect of the Shell, which runs before
 * any child's passive effect, so it is ahead of the twin HUD's capture
 * listener; a presenter key stops there (stopImmediatePropagation) and never
 * reaches the HUD's view-mode digits or the bubble-phase shortcuts (the
 * shell's Escape that clears the selection, the globe's Escape that stops
 * following). Every other key passes through untouched.
 */
import { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useUI } from '@/state/store';
import { createAppDeps } from './deps';
import { interpretKey } from './keyboard';
import { createPresenterSession } from './session';
import { usePresenter } from './store';

/**
 * True when a visible dialog other than the presenter help is open: Escape is
 * left to it. Hidden ones do not count (Cesium keeps its credit lightbox, a
 * role="dialog", in the DOM at all times).
 */
function otherDialogOpen(): boolean {
  for (const el of document.querySelectorAll('[role="dialog"], [role="alertdialog"]')) {
    if (el.closest('[data-presenter-help]')) continue;
    if (el.getAttribute('data-state') === 'closed') continue;
    if (!el.checkVisibility()) continue;
    return true;
  }
  return false;
}

export function usePresenterKeys(): void {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  // The location the router has rendered: BrowserRouter commits a navigation in a transition,
  // later than window.location changes, and the beats' profile switch waits for it.
  const location = useLocation();
  const renderedSearchRef = useRef(location.search);

  useLayoutEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useLayoutEffect(() => {
    renderedSearchRef.current = location.search;
  }, [location]);

  useLayoutEffect(() => {
    const session = createPresenterSession(
      createAppDeps(
        (to) => navigateRef.current(to),
        () => renderedSearchRef.current,
      ),
    );

    const onKey = (e: KeyboardEvent) => {
      const focused = document.activeElement;
      const command = interpretKey(
        {
          key: e.key,
          ctrlKey: e.ctrlKey,
          metaKey: e.metaKey,
          altKey: e.altKey,
          target: e.target instanceof HTMLElement ? e.target : null,
        },
        {
          presenter: useUI.getState().presenter,
          focused: focused instanceof HTMLElement ? focused : null,
          helpOpen: usePresenter.getState().helpOpen,
          dialogOpen: otherDialogOpen(),
        },
      );
      if (!command) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (e.repeat) return;
      session.run(command);
    };

    const offPresenter = useUI.subscribe((state, prev) => {
      if (state.presenter !== prev.presenter) session.presenterChanged(state.presenter);
    });
    const onPageHide = () => session.releaseFreeze();

    window.addEventListener('keydown', onKey, true);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('pagehide', onPageHide);
      offPresenter();
      session.dispose();
    };
  }, []);
}
